<script setup lang="ts">
import { HomeSchema } from '~/schemas/home';

const { data: page } = await useAsyncData(() => 
  queryCollection('HomePage').first()
);

useSeoMeta({
  title: page.value?.metaTitle,
  description: page.value?.metaDescription,
  ogUrl: page.value?.linkCanonical,
  ogTitle: page.value?.title,
  ogDescription: page.value?.description,
});

useHead({
  link: [
    { rel: 'canonical', href: page.value?.linkCanonical },
    {
      rel: 'preload',
      as: 'image',
      href: '/svg/new/backgrounds/mesh-08-mobile.svg',
      fetchpriority: 'high',
    },
    {
      rel: 'preload',
      as: 'image',
      href: '/svg/new/backgrounds/mesh-08.svg',
      fetchpriority: 'high',
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      textContent: JSON.stringify(HomeSchema)
    },
  ],
});


const modalsStore = useModalsStore();

const CountUp = defineAsyncComponent(() => import('vue-countup-v3'));

const splitedTitle = computed(() => page.value!.title.split('*'));
const highlightedWords = computed(() => splitedTitle.value[1]?.split('/'));

const { displayed } = useTypewriter({
  words: highlightedWords.value ?? [],
  typeSpeed: 180,
  deleteSpeed: 50,
  loop: true,
  delayBetweenWords: 3000,
});

function handleOpenModal() {
  modalsStore.openModal('CTA');
};
</script>

<template>
  <template v-if="page">
    <SectionsBase class="hero-container">
      <div class="container flex-center flex-col gap-8 lg:gap-10">
        <h1 class="text-center">
          {{ splitedTitle[0] }}
          <span 
            class="text-primary-600 inline-block w-23 lg:w-35 text-center leading-9.75 highlight before:w-full 
              before:mt-1 lg:before:mt-0 lg:before:w-50 lg:before:-mr-8 before:z-1"
          >
            {{ displayed }}
            <span class="cursor">|</span>
          </span>
          {{ splitedTitle[splitedTitle?.length - 1] }}
        </h1>
        <p class="text-center h4 text-gray-500">{{ page.description }}</p>
        <div class="flex flex-col lg:flex-row items-center gap-4">
          <Button
            :append-icon="{ name: 'solar:round-alt-arrow-left-bold' }"
            size="Large"
            pill
            @click="handleOpenModal"
            class="w-49.5 relative"
          >
            <IconsCurvedArrow class="hidden lg:block absolute top-22 -right-48 scale-y-[-1] origin-top -rotate-35 text-secondary-600 pointer-events-none" />
            دریافت دموی رایگان
          </Button>
          <Button
            type="Outline"
            :append-icon="{ name: 'solar:round-alt-arrow-left-bold' }"
            size="Large"
            to="/prices/"
            link
            pill
            class="w-49.5 [&_.btn-slot-parent]:w-full [&_.btn-slot-parent]:justify-between!"
          >
            مشاهده تعرفه‌ها
          </Button>
        </div>
        <div class="flex-center gap-3">
          <div class="flex items-center">
            <NuxtImg 
                v-for="img, index in page.items"
                :src="img"
                alt="کاربر نواتل"
                width="40"
                height="40"
                class="w-10 h-10 object-cover rounded-full"
                :class="index ? '-mr-3' : null"
                :preload="{ fetchPriority: 'high' }"
                fetchpriority="high"
                loading="eager"
            />
          </div>
          <div>
            <span class="block h4">
              <ClientOnly>
                <CountUp
                  end-val="29768"
                  :duration="3"
                  class="inline"
                />
              </ClientOnly>
              +
            </span>
            <span class="text-body2 text-gray-400">کسب‌وکار فعال</span>
          </div>
        </div>
      </div>
    </SectionsBase>

    <LazyIdleSectionsLogos hydrate-on-idle />

    <LazyVisibleSectionsProductsHome
      hydrate-on-visible
      :title="page.products.title"
      :items="page.products.items"
    />

    <LazyVisibleSectionsFeaturesHome
      hydrate-on-visible
      :title="page.features.title"
      :items="page.features.items"
    />

    <LazyVisibleSectionsFacilitiesHome
      hydrate-on-visible
      :title="page.facilities.title"
      :desc="page.facilities.desc"
    />

    <LazyVisibleSectionsPermissions hydrate-on-visible />

    <LazyVisibleSectionsSolutionsCarousel
      hydrate-on-visible
      :title="page.solutions.title"
      :desc="page.solutions.desc"
      :items="page.solutions.items"
    />

    <LazyVisibleSectionsNavatelOverview
      hydrate-on-visible
      :title="page.navatelOverview.title"
      :subTitle="page.navatelOverview.subTitle"
      :desc="page.navatelOverview.desc"
    />

    <LazyVisibleSectionsPartners hydrate-on-visible />

    <LazyVisibleSectionsPlatformAccess 
      hydrate-on-visible 
      :title="page.platformAccess.title"
      :desc="page.platformAccess.desc"
    />

    <LazyVisibleSectionsComments hydrate-on-visible />

    <SectionsFAQ
      :title="page.FAQ.title"
      :items="page.FAQ.items"
    />

    <LazyVisibleSectionsBlog 
      hydrate-on-visible 
      :title="page.blog.title"
      :desc="page.blog.desc"
      :items="page.blog.items"
    />

    <LazySectionsCTA 
      :title="page.CTA.title" 
      :desc="page.CTA.desc"
    />
  </template>
</template>

<style scoped>
@reference "~/assets/css/main.css";

.hero-container {
  @apply
  bg-[url('/svg/new/backgrounds/mesh-08-mobile.svg')] lg:bg-[url('/svg/new/backgrounds/mesh-08.svg')] 
  bg-size-[100%_auto] lg:bg-size-[1536px_auto] bg-no-repeat pt-25! pb-35! lg:pt-26! lg:pb-32.5! bg-top 
  lg:bg-center relative
}
.hero-container::before {
  @apply
  content-[''] absolute w-full h-5 top-0 right-0 bg-linear-to-b from-white to-transparent
}
.hero-container::after {
  @apply
  content-[''] absolute w-full h-5 bottom-0 right-0 bg-linear-to-t from-white to-transparent
}

.cursor {
  animation: blink 1s infinite;
  @apply
  inline -mr-2 lg:-mr-2.75 font-normal
}

@keyframes blink {
  50% { opacity: 0; }
}
</style>